import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { clearCache } from "@/lib/cache";

const AuthContext = createContext(null);
const USER_KEY = "bazaro_user";

const cachedUser = () => {
  if (!localStorage.getItem("bazaro_token")) return undefined;
  try { return JSON.parse(localStorage.getItem(USER_KEY)) || undefined; } catch { return undefined; }
};

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(cachedUser); // undefined = loading, null = guest

  const setUser = useCallback((next) => {
    setUserState((prev) => {
      const u = typeof next === "function" ? next(prev) : next;
      if (u) localStorage.setItem(USER_KEY, JSON.stringify(u)); else localStorage.removeItem(USER_KEY);
      return u;
    });
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      clearCache();
      setUser(null);
    }
  }, [setUser]);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      // AuthCallback handles session exchange
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Logout on another device kills this session too: re-check on focus and every 30s.
  useEffect(() => {
    if (!user) return undefined;
    const verify = () => api.get("/auth/me").catch((err) => {
      if (err.response?.status === 401) { localStorage.removeItem("bazaro_token"); clearCache(); setUser(null); }
    });
    const id = setInterval(verify, 30000);
    window.addEventListener("focus", verify);
    return () => { clearInterval(id); window.removeEventListener("focus", verify); };
  }, [!!user, setUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendOtp = async (payload) => {
    const { data } = await api.post("/auth/otp/send", payload);
    return data;
  };

  const verifyOtp = async (email, code) => {
    const { data } = await api.post("/auth/otp/verify", { email, code });
    if (data.token) localStorage.setItem("bazaro_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const startSession = (data) => {
    if (data.verification_required || data.pending_approval) return data;
    if (data.token) localStorage.setItem("bazaro_token", data.token);
    clearCache();
    setUser(data.user);
    return data.user;
  };

  const login = async (email, password) => startSession((await api.post("/auth/login", { email, password })).data);

  const register = async (payload) => startSession((await api.post("/auth/register", payload)).data);

  const verifyEmail = async (email, code) => startSession((await api.post("/auth/verify-email", { email, code })).data);

  const logout = async () => {
    const token = localStorage.getItem("bazaro_token");
    localStorage.removeItem("bazaro_token");
    clearCache();
    setUser(null);
    try { await api.post("/auth/logout", null, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined); } catch {}
  };

  const setFav = (listingId, on) => setUser((u) => {
    if (!u) return u;
    const favs = new Set(u.favorites || []);
    if (on) favs.add(listingId); else favs.delete(listingId);
    return { ...u, favorites: [...favs] };
  });

  const toggleFavorite = async (listingId) => {
    const was = !!user?.favorites?.includes(listingId);
    setFav(listingId, !was);
    try {
      const { data } = await api.post(`/favorites/${listingId}`);
      setFav(listingId, data.favorited);
      return data.favorited;
    } catch (err) {
      setFav(listingId, was);
      throw err;
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, checkAuth, sendOtp, verifyOtp, login, register, verifyEmail, logout, toggleFavorite }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
